import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Menu,
  X,
  Home,
  Package,
  Heart,
  MapPin,
  LogIn,
  UserPlus,
  LogOut,
  ShieldCheck,
  FileText,
  ShieldQuestion,
  RotateCcw,
  Phone,
  Instagram,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { checkAdminAccess } from "@/lib/admin/admin.functions";
import { useStoreSettings } from "@/lib/settings.functions";

function MenuLink({
  to,
  icon: Icon,
  children,
  onClick,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-eg-dark hover:bg-eg-gold/10 hover:text-eg-goldtext transition-colors"
    >
      <Icon className="h-4 w-4" />
      {children}
    </Link>
  );
}

function MenuGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      {children}
    </div>
  );
}

export function SiteMenu() {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const signOut = useAuth((s) => s.signOut);
  const settings = useStoreSettings();

  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin"],
    queryFn: () => checkAdminAccess().then(() => true),
    enabled: !!user,
    retry: false,
  });

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Abrir menu"
        className="rounded-full p-2 hover:bg-white/10 transition-colors"
      >
        <Menu className="h-6 w-6" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={close} aria-hidden />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            className="absolute left-0 top-0 flex h-full w-full max-w-xs flex-col bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-4">
              <span className="font-display text-lg font-bold text-eg-dark">Menu</span>
              <button
                type="button"
                onClick={close}
                aria-label="Fechar"
                className="rounded p-1 hover:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav aria-label="Menu principal" className="flex-1 space-y-6 overflow-y-auto px-2 py-4">
              <MenuGroup title="Loja">
                <MenuLink to="/" icon={Home} onClick={close}>
                  Início
                </MenuLink>
              </MenuGroup>

              <MenuGroup title="Minha Conta">
                {user ? (
                  <>
                    <MenuLink to="/minha-conta/pedidos" icon={Package} onClick={close}>
                      Meus Pedidos
                    </MenuLink>
                    <MenuLink to="/minha-conta/favoritos" icon={Heart} onClick={close}>
                      Meus Favoritos
                    </MenuLink>
                    <MenuLink to="/minha-conta/enderecos" icon={MapPin} onClick={close}>
                      Meus Endereços
                    </MenuLink>
                    <button
                      type="button"
                      onClick={async () => {
                        close();
                        await signOut();
                        navigate({ to: "/" });
                      }}
                      className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-eg-dark hover:bg-eg-gold/10 hover:text-eg-goldtext transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Sair
                    </button>
                  </>
                ) : (
                  <>
                    <MenuLink to="/login" icon={LogIn} onClick={close}>
                      Entrar
                    </MenuLink>
                    <MenuLink to="/cadastro" icon={UserPlus} onClick={close}>
                      Criar Conta
                    </MenuLink>
                  </>
                )}
              </MenuGroup>

              {isAdmin && (
                <MenuGroup title="Administração">
                  <MenuLink to="/admin" icon={ShieldCheck} onClick={close}>
                    Painel Admin
                  </MenuLink>
                </MenuGroup>
              )}

              <MenuGroup title="Institucional">
                <MenuLink to="/termos" icon={FileText} onClick={close}>
                  Termos de Uso
                </MenuLink>
                <MenuLink to="/privacidade" icon={ShieldQuestion} onClick={close}>
                  Política de Privacidade
                </MenuLink>
                <MenuLink to="/trocas-devolucoes" icon={RotateCcw} onClick={close}>
                  Trocas e Devoluções
                </MenuLink>
                <a
                  href={`https://wa.me/${settings.whatsapp_number}`}
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-eg-dark hover:bg-eg-gold/10 hover:text-eg-goldtext transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  WhatsApp
                </a>
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-eg-dark hover:bg-eg-gold/10 hover:text-eg-goldtext transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                  Instagram
                </a>
                <a
                  href={settings.institutional_url}
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-eg-dark hover:bg-eg-gold/10 hover:text-eg-goldtext transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  Site Institucional
                </a>
              </MenuGroup>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
